import React, { useState, useRef } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import { 
  Save, 
  X, 
  Eye, 
  Code, 
  FileText, 
  Settings,
  HelpCircle,
  Copy
} from 'lucide-react'

interface Template {
  id?: string
  nome: string
  tipo: string
  template_html: string
  variaveis_mapeamento: any
  ativo: boolean
  created_at?: string
  updated_at?: string
}

interface TemplateEditorProps {
  template?: Template | null
  onSave: (templateData: Omit<Template, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  onClose: () => void
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ 
  template, 
  onSave, 
  onClose 
}) => {
  const [formData, setFormData] = useState({
    nome: template?.nome || '',
    tipo: template?.tipo || 'marketing_digital',
    template_html: template?.template_html || '',
    variaveis_mapeamento: template?.variaveis_mapeamento || {},
    ativo: template?.ativo !== undefined ? template.ativo : true
  })
  
  const [activeTab, setActiveTab] = useState('editor')
  const [saving, setSaving] = useState(false)
  const editorRef = useRef<any>(null)

  // Variáveis disponíveis no sistema - TODOS os campos cadastrais do cliente
  const availableVariables = [
    // Dados Pessoais
    { 
      name: 'CLIENTE_NOME_COMPLETO', 
      description: 'Nome completo do cliente',
      example: 'João Silva Santos',
      category: 'pessoal'
    },
    { 
      name: 'CLIENTE_CELULAR', 
      description: 'Número do celular do cliente',
      example: '(11) 99999-9999',
      category: 'pessoal'
    },
    { 
      name: 'CLIENTE_DATA_NASCIMENTO', 
      description: 'Data de nascimento do cliente',
      example: '15/03/1985',
      category: 'pessoal'
    },
    { 
      name: 'CLIENTE_TELEFONE', 
      description: 'Telefone fixo do cliente',
      example: '(11) 3333-4444',
      category: 'pessoal'
    },
    { 
      name: 'CLIENTE_EMAIL', 
      description: 'Email do cliente',
      example: 'joao@email.com',
      category: 'pessoal'
    },

    // Dados Empresariais
    { 
      name: 'CLIENTE_RAZAO_SOCIAL', 
      description: 'Razão social da empresa',
      example: 'João Silva Santos ME',
      category: 'empresarial'
    },
    { 
      name: 'CLIENTE_NOME_EMPRESA', 
      description: 'Nome fantasia da empresa',
      example: 'Silva Consultoria',
      category: 'empresarial'
    },
    { 
      name: 'CLIENTE_CNPJ', 
      description: 'CNPJ da empresa',
      example: '12.345.678/0001-90',
      category: 'empresarial'
    },
    { 
      name: 'CLIENTE_DATA_INAUGURACAO', 
      description: 'Data de inauguração da empresa',
      example: '10/01/2020',
      category: 'empresarial'
    },
    { 
      name: 'CLIENTE_CAPITAL_SOCIAL', 
      description: 'Capital social da empresa',
      example: 'R$ 50.000,00',
      category: 'empresarial'
    },
    { 
      name: 'CLIENTE_COLABORADORES', 
      description: 'Quantidade de colaboradores',
      example: '5 funcionários',
      category: 'empresarial'
    },

    // Dados de Endereço
    { 
      name: 'CLIENTE_ENDERECO', 
      description: 'Endereço completo do cliente',
      example: 'Rua das Flores, 123, Centro',
      category: 'endereco'
    },
    { 
      name: 'CLIENTE_CEP', 
      description: 'CEP do cliente',
      example: '01234-567',
      category: 'endereco'
    },
    { 
      name: 'CLIENTE_BAIRRO', 
      description: 'Bairro do cliente',
      example: 'Centro',
      category: 'endereco'
    },
    { 
      name: 'CLIENTE_CIDADE', 
      description: 'Cidade do cliente',
      example: 'São Paulo',
      category: 'endereco'
    },
    { 
      name: 'CLIENTE_UF', 
      description: 'Estado (UF) do cliente',
      example: 'SP',
      category: 'endereco'
    },

    // Dados Financeiros
    { 
      name: 'CLIENTE_FATURAMENTO_EMPRESA', 
      description: 'Faturamento atual da empresa',
      example: 'R$ 100.000,00/mês',
      category: 'financeiro'
    },
    { 
      name: 'CLIENTE_FATURAMENTO_DESEJADO', 
      description: 'Faturamento desejado',
      example: 'R$ 200.000,00/mês',
      category: 'financeiro'
    },
    { 
      name: 'CLIENTE_RESPONSAVEL_FINANCEIRO', 
      description: 'Nome do responsável financeiro',
      example: 'Maria Silva',
      category: 'financeiro'
    },
    { 
      name: 'CLIENTE_EMAIL_FINANCEIRO', 
      description: 'Email do responsável financeiro',
      example: 'financeiro@empresa.com',
      category: 'financeiro'
    },
    { 
      name: 'CLIENTE_TELEFONE_FINANCEIRO', 
      description: 'Telefone do responsável financeiro',
      example: '(11) 8888-7777',
      category: 'financeiro'
    },

    // Dados Digitais/Marketing
    { 
      name: 'CLIENTE_SITE', 
      description: 'Website da empresa',
      example: 'www.empresa.com.br',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_INSTAGRAM', 
      description: 'Instagram da empresa',
      example: '@empresa_oficial',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_FACEBOOK', 
      description: 'Facebook da empresa',
      example: 'facebook.com/empresa',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_WHATSAPP_ANUNCIOS', 
      description: 'WhatsApp para anúncios',
      example: '(11) 99999-8888',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_CIDADES_ANUNCIAR', 
      description: 'Cidades para anunciar',
      example: 'São Paulo, Guarulhos, Osasco',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_BAIRROS_ANUNCIAR', 
      description: 'Bairros para anunciar',
      example: 'Centro, Vila Madalena, Pinheiros',
      category: 'digital'
    },
    { 
      name: 'CLIENTE_PUBLICO_IDEAL', 
      description: 'Descrição do público ideal',
      example: 'Empresários de 30-50 anos',
      category: 'digital'
    },

    // Dados do Contrato
    { 
      name: 'SERVICOS_CONTRATADOS', 
      description: 'Lista HTML dos serviços contratados',
      example: '<ul><li>Google Ads</li><li>SEO</li></ul>',
      category: 'contrato'
    },
    { 
      name: 'DATA_ATUAL', 
      description: 'Data atual formatada',
      example: '23/01/2026',
      category: 'contrato'
    },
    { 
      name: 'NUMERO_CONTRATO', 
      description: 'Número do contrato gerado automaticamente',
      example: 'M4-2026-001',
      category: 'contrato'
    },

    // Dados Calculados/Automáticos
    { 
      name: 'CLIENTE_NOME_RAZAO', 
      description: 'Nome completo ou razão social (automático)',
      example: 'João Silva ou Empresa LTDA',
      category: 'automatico'
    },
    { 
      name: 'CLIENTE_QUALIFICACAO', 
      description: 'Pessoa física ou jurídica (calculado)',
      example: 'pessoa física',
      category: 'automatico'
    },
    { 
      name: 'CLIENTE_DOCUMENTO', 
      description: 'CPF ou CNPJ (calculado)',
      example: '123.456.789-00',
      category: 'automatico'
    },
    { 
      name: 'CLIENTE_ENDERECO_COMPLETO', 
      description: 'Endereço completo formatado',
      example: 'Rua das Flores, 123, Centro, São Paulo/SP',
      category: 'automatico'
    },
    { 
      name: 'CLIENTE_REPRESENTANTE', 
      description: 'Nome do representante/responsável',
      example: 'João Silva',
      category: 'automatico'
    }
  ]

  const handleSave = async () => {
    if (!formData.nome.trim() || !formData.template_html.trim()) {
      alert('Nome e conteúdo do template são obrigatórios')
      return
    }

    setSaving(true)
    try {
      await onSave(formData)
    } finally {
      setSaving(false)
    }
  }

  const handleEditorChange = (content: string) => {
    setFormData(prev => ({ ...prev, template_html: content }))
  }

  const insertVariable = (variableName: string) => {
    const editor = editorRef.current
    if (editor) {
      editor.insertContent(`{{${variableName}}}`)
    }
  }

  const duplicateTemplate = () => {
    setFormData(prev => ({
      ...prev,
      nome: `${prev.nome} (Cópia)`,
      ativo: true
    }))
  }

  const tabs = [
    { id: 'editor', label: 'Editor', icon: FileText },
    { id: 'variables', label: 'Variáveis', icon: Settings },
    { id: 'preview', label: 'Preview', icon: Eye },
    { id: 'code', label: 'Código', icon: Code }
  ]

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white min-h-[95vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {template ? 'Editar Template' : 'Novo Template'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {template ? `Editando: ${template.nome}` : 'Criar um novo template de contrato'}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {template && (
              <button
                onClick={duplicateTemplate}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex h-full space-x-6">
          {/* Configurações do Template */}
          <div className="w-80 space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-4">Configurações do Template</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Template *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Marketing Digital - Padrão"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Contrato
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="marketing_digital">Marketing Digital</option>
                    <option value="consultoria">Consultoria</option>
                    <option value="desenvolvimento">Desenvolvimento</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="projeto_especifico">Projeto Específico</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="ativo"
                    checked={formData.ativo}
                    onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="ativo" className="text-sm font-medium text-gray-700">
                    Template ativo
                  </label>
                </div>
              </div>
            </div>

            {/* Variáveis Disponíveis */}
            <div className="bg-gray-50 p-4 rounded-lg border">
              <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Variáveis Disponíveis
              </h4>
              <p className="text-xs text-gray-600 mb-4">
                Arraste as variáveis para o editor →
              </p>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {/* Grupo Dados Pessoais */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Dados Pessoais
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'pessoal').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-md cursor-move hover:bg-blue-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-blue-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-blue-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Dados Empresariais */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Dados Empresariais
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'empresarial').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-green-50 border border-green-200 rounded-md cursor-move hover:bg-green-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-green-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-green-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-green-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Endereço */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Endereço
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'endereco').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-purple-50 border border-purple-200 rounded-md cursor-move hover:bg-purple-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-purple-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-purple-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Financeiro */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Financeiro
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'financeiro').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-yellow-50 border border-yellow-200 rounded-md cursor-move hover:bg-yellow-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-yellow-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-yellow-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Digital/Marketing */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                    Digital/Marketing
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'digital').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-pink-50 border border-pink-200 rounded-md cursor-move hover:bg-pink-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-pink-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-pink-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-pink-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Contrato */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Contrato
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'contrato').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-orange-50 border border-orange-200 rounded-md cursor-move hover:bg-orange-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-orange-800">
                            {variable.name}
                          </span>
                          <span className="text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-orange-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Grupo Automático */}
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                    Calculados
                  </h5>
                  <div className="space-y-1">
                    {availableVariables.filter(v => v.category === 'automatico').map((variable) => (
                      <div
                        key={variable.name}
                        draggable="true"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{${variable.name}}}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-md cursor-move hover:bg-gray-100 transition-colors group"
                        title={variable.description}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-gray-800">
                            {variable.name.replace('CLIENTE_', '')}
                          </span>
                          <span className="text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            ⋮⋮
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5 truncate">
                          {variable.example}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Editor Principal */}
          <div className="flex-1">

            {/* Editor */}
            <div className="flex-1">
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-8">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                      </button>
                    )
                  })}
                </nav>
              </div>

              {/* Conteúdo das Tabs */}
              <div className="h-96">
                {activeTab === 'editor' && (
                  <div 
                    className="h-full border-2 border-dashed border-gray-300 rounded-lg relative"
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.currentTarget.classList.add('border-orange-400', 'bg-orange-50')
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50')
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50')
                      const variable = e.dataTransfer.getData('text/plain')
                      if (editorRef.current && variable) {
                        editorRef.current.insertContent(variable)
                      }
                    }}
                  >
                    <Editor
                      apiKey="g2byz9d0oft9qwfuqs19csc49weref28tgvev6ivsz5iptmw"
                      onInit={(_, editor) => {
                        editorRef.current = editor
                        
                        // Configurar drop no TinyMCE
                        editor.on('drop', (e) => {
                          const variable = e.dataTransfer?.getData('text/plain')
                          if (variable && variable.startsWith('{{') && variable.endsWith('}}')) {
                            e.preventDefault()
                            editor.insertContent(variable)
                          }
                        })
                        
                        editor.on('dragover', (e) => {
                          e.preventDefault()
                        })
                      }}
                      value={formData.template_html}
                      onEditorChange={handleEditorChange}
                      init={{
                        height: 450,
                        menubar: false,
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image link | removeformat | help',
                        // Configuração de upload de imagens
                        images_upload_handler: async (blobInfo, progress) => {
                          return new Promise((resolve, reject) => {
                            const formData = new FormData()
                            formData.append('image', blobInfo.blob(), blobInfo.filename())
                            formData.append('template_id', template?.id || 'new')

                            fetch('/api/gestao/contrato-imagens', {
                              method: 'POST',
                              body: formData
                            })
                            .then(response => response.json())
                            .then(result => {
                              if (result.success) {
                                resolve(result.url)
                              } else {
                                reject(result.error || 'Erro no upload')
                              }
                            })
                            .catch(error => {
                              reject('Erro de conexão: ' + error.message)
                            })
                          })
                        },
                        images_upload_base_path: '',
                        images_upload_credentials: true,
                        automatic_uploads: true,
                        paste_data_images: true,
                        content_style: `
                          body { 
                            font-family: Arial, sans-serif; 
                            font-size: 12px; 
                            line-height: 1.4; 
                            margin: 20px;
                            text-align: justify;
                          }
                          .header { text-align: center; font-weight: bold; margin-bottom: 30px; }
                          .clause { margin-bottom: 20px; }
                          .clause-title { font-weight: bold; margin-bottom: 10px; }
                          img { max-width: 100%; height: auto; margin: 10px 0; }
                        `,
                        language: 'pt_BR',
                        branding: false
                      }}
                    />
                    
                    {/* Indicador de Drop Zone */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-0 transition-opacity duration-200" id="drop-indicator">
                      <div className="bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg">
                        Solte a variável aqui
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'variables' && (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Agora com Drag & Drop:</strong> Arraste as variáveis do painel lateral diretamente para o editor!
                      </p>
                    </div>
                    
                    {availableVariables.map((variable) => (
                      <div key={variable.name} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{`{${variable.name}}`}</h4>
                            <p className="text-sm text-gray-600 mt-1">{variable.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              <strong>Exemplo:</strong> {variable.example}
                            </p>
                          </div>
                          <button
                            onClick={() => insertVariable(variable.name)}
                            className="ml-3 px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                          >
                            Inserir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'preview' && (
                  <div className="border rounded-lg p-6 bg-white max-h-96 overflow-y-auto">
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: formData.template_html.replace(
                          /\{\{([^}]+)\}\}/g, 
                          '<span class="bg-yellow-200 px-1 rounded">$1</span>'
                        )
                      }}
                    />
                  </div>
                )}

                {activeTab === 'code' && (
                  <textarea
                    value={formData.template_html}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_html: e.target.value }))}
                    className="w-full h-96 p-4 border border-gray-300 rounded-md font-mono text-sm"
                    placeholder="Cole ou digite o HTML do template aqui..."
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplateEditor
